import { KeyboardEvent, useContext, useEffect, useRef } from 'react';
import Link from 'next/link';
import { ExclamationCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { Alert, combineClassNames } from '@arkestro/arkestro-design-system';
import useTranslation from 'next-translate/useTranslation';

import { LineItemsContext, SHOULD_REFRESH } from '@providers/LineItemsProvider';
import { useAnchorPriceGenerationStatus } from '@features/suggested-price-column/hooks/use-anchor-price-generation-status';
import {
  ANCHOR_PRICE_AUTO_APPLY_WINDOW_MS,
  ANCHOR_PRICE_GENERATION_STATUS,
  anchorPriceGenerationStore,
} from '@features/suggested-price-column/store/anchor-price-generation-store';

import styles from './suggested-price-generation-banner.module.scss';

/**
 * Status banner for suggested-price generation, rendered above the buyer line items grid.
 *
 * The no-suppliers state takes precedence over every generation status: anchor price generation
 * iterates only the orgs a buyer has committed to the event, so an event whose suppliers are all
 * potential participants produces nothing regardless of what the store says. It keys off
 * hasInvitedOrScheduledSuppliers rather than hasSuppliers for exactly that reason — hasSuppliers
 * counts potential participants too, and would hide this banner on an event that cannot generate.
 * That state is not dismissible either — only inviting or queueing a supplier resolves it, so the
 * link to the Suppliers tab is the way out rather than an X.
 *
 * A stale warning with nothing left unsaved means the edit that invalidated the prices is gone,
 * saved or discarded. Discarding an edit made while regenerated prices were still waiting to be
 * applied hands the apply prompt back rather than clearing the banner outright — otherwise those
 * prices stay invisible until a reload. Restoring READY cannot re-arm the auto-apply effect:
 * reaching a lingering READY at all means regeneration finished outside the fast window, so its
 * generatingStartedAt check has already lapsed.
 */
export const SuggestedPriceGenerationBanner = () => {
  const { t } = useTranslation('common');
  const { reset, status } = useAnchorPriceGenerationStatus();
  const { dispatch, handleSaveChanges, hasChanges, request, state } = useContext(LineItemsContext);
  const { hasInvitedOrScheduledSuppliers } = state.event;
  const handlersRef = useRef({ dispatch, handleSaveChanges, hasChanges });
  handlersRef.current = { dispatch, handleSaveChanges, hasChanges };

  useEffect(() => {
    if (status !== ANCHOR_PRICE_GENERATION_STATUS.READY) return;
    const startedAt = anchorPriceGenerationStore.getGeneratingStartedAt();
    if (startedAt == null) return;
    if (Date.now() - startedAt >= ANCHOR_PRICE_AUTO_APPLY_WINDOW_MS) return;

    const { dispatch: currentDispatch, handleSaveChanges: currentSave, hasChanges: currentHasChanges } = handlersRef.current;
    if (currentHasChanges) currentSave();
    else currentDispatch({ type: SHOULD_REFRESH });
    anchorPriceGenerationStore.setStatus(ANCHOR_PRICE_GENERATION_STATUS.APPLIED);
  }, [status]);

  useEffect(() => {
    if (status === ANCHOR_PRICE_GENERATION_STATUS.STALE && !hasChanges) {
      anchorPriceGenerationStore.setStatus(
        anchorPriceGenerationStore.getHasUnappliedGeneration()
          ? ANCHOR_PRICE_GENERATION_STATUS.READY
          : ANCHOR_PRICE_GENERATION_STATUS.IDLE,
      );
    }
  }, [status, hasChanges]);

  if (!hasInvitedOrScheduledSuppliers) {
    return (
      <Alert
        action={(
          <Link className={styles.suppliersLink} href={`/bid_requests/${request.id}/suppliers`}>
            {t('line_items.suggested_prices.go_to_suppliers')}
          </Link>
        )}
        className={styles.alert}
        closable={false}
        message={t('line_items.suggested_prices.no_suppliers')}
        showIcon
        type='info'
      />
    );
  }

  if (status === ANCHOR_PRICE_GENERATION_STATUS.IDLE) return null;

  const handleApply = () => {
    if (hasChanges) handleSaveChanges();
    else dispatch({ type: SHOULD_REFRESH });
    reset();
  };

  const handleReadyKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleApply();
    }
  };

  const cta = hasChanges
    ? t('line_items.suggested_prices.click_to_save_and_apply')
    : t('line_items.suggested_prices.click_to_apply');

  const readyMessage = (
    <span
      className={styles.readyMessage}
      onClick={handleApply}
      onKeyDown={handleReadyKeyDown}
      role='button'
      tabIndex={0}
    >
      {t('line_items.suggested_prices.ready')} <span className={styles.cta}>{cta}</span>
    </span>
  );

  const alertPropsByStatus = {
    [ANCHOR_PRICE_GENERATION_STATUS.APPLIED]: {
      className: styles.alert,
      closable: true,
      message: t('line_items.suggested_prices.applied'),
      onClose: reset,
      type: 'info' as const,
    },
    [ANCHOR_PRICE_GENERATION_STATUS.GENERATING]: {
      className: combineClassNames(styles.alert, styles.generatingAlert),
      closable: true,
      icon: <LoadingOutlined spin />,
      message: t('line_items.suggested_prices.generating'),
      onClose: reset,
      type: 'info' as const,
    },
    [ANCHOR_PRICE_GENERATION_STATUS.READY]: {
      className: styles.alert,
      closable: false,
      message: readyMessage,
      type: 'info' as const,
    },
    [ANCHOR_PRICE_GENERATION_STATUS.STALE]: {
      className: styles.alert,
      closable: true,
      icon: <ExclamationCircleFilled />,
      message: t('line_items.suggested_prices.stale_warning'),
      onClose: reset,
      type: 'warning' as const,
    },
  };

  return <Alert showIcon {...alertPropsByStatus[status]} />;
};
